// services/requestDemoService.js
import axios from "axios";
import host from "../../../global.jsx"; // Adjust path if needed

// Create shared axios instance with dynamic token
const api = axios.create({
  baseURL: host,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add token dynamically on every request
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("accessToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const message =
      error.response?.data?.detail ||
      error.response?.data?.message ||
      error.message ||
      "An unexpected error occurred";
    return Promise.reject(new Error(message));
  }
);

export const requestDemoService = {
  // Fetch accepted demo requests
  getAcceptedDemos: async () => {
    const response = await api.get("/get-accepted-demos");
    return Array.isArray(response.data) ? response.data : [];
  },

  // Fetch trial expirations (if separate endpoint)
  getTrialExpirations: async () => {
    const response = await api.get("/get_trial_expirations");
    return Array.isArray(response.data) ? response.data : [];
  },

  // Fetch all org admins and filter expiring ones on frontend (or move to backend)
  getExpiringOrgAdmins: async () => {
    const response = await api.get("/getAllOrgAdmins");
    const admins = Array.isArray(response.data) ? response.data : [];

    const today = new Date();
    const fifteenDaysFromNow = new Date();
    fifteenDaysFromNow.setDate(today.getDate() + 15);

    return admins.filter((admin) => {
      if (!admin.end_date) return false;
      const endDate = new Date(admin.end_date);
      return endDate <= fifteenDaysFromNow && endDate >= today;
    });
  },

  // Create admin from demo request
  createAdminFromDemo: async (demo) => {
    const formData = new FormData();
    formData.append("organization_name", extractCompanyNameFromEmail(demo.workEmail));
    formData.append("org_admin_name", demo.name);
    formData.append("org_admin_email", demo.workEmail);
    formData.append("org_admin_contact", demo.phoneNumber || "");
    formData.append("total_hr_allocated", "5");
    formData.append("timezone", "Asia/Kolkata");
    formData.append("account_type", "Trial");

    const response = await api.post("/create_new_org_admin", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    // Mark demo as processed
    try {
      await api.put(`/update-demo-status/${demo._id}/processed`);
    } catch (err) {
      console.warn("Failed to update demo status (non-critical):", err.message);
    }

    return response.data;
  },

  // Suspend org admin
  suspendOrgAdmin: async (orgAdminId, reason) => {
    return await api.post(
      `/suspend_org_admin/${orgAdminId}`,
      { reason },
      {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      }
    );
  },

  // Reactivate org admin
  reactivateOrgAdmin: async (orgAdminId) => {
    return await api.post(`/reactivate_org_admin/${orgAdminId}`, {});
  },
};

// Helper function (can stay here or move to utils)
export const extractCompanyNameFromEmail = (email) => {
  if (!email) return "N/A";
  const domain = email.split("@")[1]?.split(".")[0];
  return domain ? domain.charAt(0).toUpperCase() + domain.slice(1) : "N/A";
};

export default requestDemoService;